import { X, Keyboard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePosUiStore } from '@/store/posUiStore';
import { formatShortcutKey, getShortcutRows } from '@/data/posShortcuts';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function PosShortcutsHelpModal({ open, onClose }: Props) {
  const { t } = useTranslation();
  const shortcuts = usePosUiStore((s) => s.shortcuts);
  if (!open) return null;

  const rows = getShortcutRows(shortcuts);
  const globalRows = rows.filter((r) => r.context === 'global');
  const cartRows = rows.filter((r) => r.context === 'cart');
  const paymentRows = rows.filter((r) => r.context === 'payment');

  const renderRows = (list: typeof rows) => (
    <ul className="space-y-2">
      {list.map((row) => (
        <li key={row.action} className="flex items-center justify-between gap-3 text-sm">
          <span className="text-gray-700">{row.label}</span>
          <kbd className="shrink-0 px-2 py-1 rounded bg-gray-100 border border-gray-200 text-xs font-mono text-gray-800">
            {row.keys}
          </kbd>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
          <div className="flex items-center gap-2">
            <Keyboard className="w-5 h-5 text-primary-600" />
            <h3 className="font-semibold text-gray-900">{t('posShortcuts.title')}</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto p-4 space-y-4">
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{t('posShortcuts.sectionPos')}</h4>
            {renderRows(globalRows)}
          </section>
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{t('posShortcuts.sectionCart')}</h4>
            {renderRows(cartRows)}
          </section>
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{t('posShortcuts.sectionPayment')}</h4>
            {renderRows(paymentRows)}
          </section>
        </div>
        <div className="px-4 py-3 border-t text-xs text-gray-500 shrink-0">
          {t('posShortcuts.footerHint', { key: formatShortcutKey(shortcuts.showShortcuts) })}
        </div>
      </div>
    </div>
  );
}
